/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/pbxCliButtonConfigApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for PBX CLI Button Configs.
    Centralizes all API calls related to PBX CLI button configurations.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  PbxCliButtonConfig,
  ListPbxCliButtonConfigsResponse,
  PbxCliButtonConfigPayload,
} from '../types';

/**
 * Fetch PBX CLI Button Configs with pagination and search
 */
export async function fetchPbxCliButtonConfigs(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}): Promise<ListPbxCliButtonConfigsResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
  });

  const url = `${buildApiUrl('/api/telephony/pbx-cli/button-configs')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch PBX CLI Button Configs');
  }

  return result;
}

/**
 * Fetch all PBX CLI Button Configs (for button grid)
 */
export async function fetchAllPbxCliButtonConfigs(): Promise<PbxCliButtonConfig[]> {
  const response = await fetch(
    buildApiUrl('/api/telephony/pbx-cli/button-configs/all'),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch PBX CLI Button Configs');
  }

  return result.data || [];
}

/**
 * Fetch single PBX CLI Button Config by ID
 */
export async function fetchPbxCliButtonConfig(
  id: string | number,
): Promise<PbxCliButtonConfig> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pbx-cli/button-configs/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch PBX CLI Button Config');
  }

  return result.data;
}

/**
 * Create new PBX CLI Button Config
 */
export async function createPbxCliButtonConfig(
  payload: PbxCliButtonConfigPayload,
): Promise<PbxCliButtonConfig> {
  const response = await fetch(
    buildApiUrl('/api/telephony/pbx-cli/button-configs'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create PBX CLI Button Config');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create PBX CLI Button Config');
  }

  return result.data;
}

/**
 * Update PBX CLI Button Config
 */
export async function updatePbxCliButtonConfig(
  id: string | number,
  payload: PbxCliButtonConfigPayload,
): Promise<PbxCliButtonConfig> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pbx-cli/button-configs/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update PBX CLI Button Config');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update PBX CLI Button Config');
  }

  return result.data;
}

/**
 * Delete PBX CLI Button Config
 */
export async function deletePbxCliButtonConfig(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/pbx-cli/button-configs/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    const message = result?.message || 'Failed to delete PBX CLI Button Config';
    throw new Error(message);
  }
}

